import { collection, doc, getDoc, setDoc, updateDoc, query, where, orderBy, limit, getDocs, serverTimestamp, increment, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Extrae hashtags de un texto utilizando expresiones regulares
 * @param text Texto del que extraer los hashtags
 * @returns Array de hashtags encontrados
 */
export const extractHashtags = (text: string): string[] => {
  const hashtags = text.match(/#\w+/g) || [];
  return hashtags;
};

/**
 * Actualiza o crea un hashtag en la colecciu00f3n trendingHashtags
 * @param hashtag Hashtag a actualizar (con #)
 * @param postId ID del post que contiene el hashtag
 */
export const updateTrendingHashtag = async (hashtag: string, postId: string): Promise<void> => {
  try {
    // Eliminar el # para usarlo como ID del documento (opcional)
    const tagId = hashtag.startsWith('#') ? hashtag.substring(1) : hashtag;
    const tagRef = doc(db, 'trendingHashtags', tagId);
    const tagSnap = await getDoc(tagRef);

    if (tagSnap.exists()) {
      // Si el hashtag ya existe, actualizamos su contador y au00f1adimos el post
      await updateDoc(tagRef, {
        count: increment(1),
        lastUsed: serverTimestamp(),
        posts: arrayUnion(postId)
      });
    } else {
      // Si el hashtag no existe, lo creamos
      await setDoc(tagRef, {
        tag: hashtag,  // Guardamos el hashtag completo con #
        count: 1,
        lastUsed: serverTimestamp(),
        posts: [postId],
        createdAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error(`Error updating trending hashtag ${hashtag}:`, error);
    throw new Error(`No se pudo actualizar el hashtag ${hashtag}`);
  }
};

/**
 * Procesa todos los hashtags de un post y los actualiza en la colecciu00f3n trendingHashtags
 * @param hashtags Array de hashtags encontrados en el post
 * @param postId ID del post que contiene los hashtags
 */
export const processHashtags = async (hashtags: string[], postId: string): Promise<void> => {
  try {
    // Procesar cada hashtag encontrado
    const promises = hashtags.map(hashtag => updateTrendingHashtag(hashtag, postId));
    await Promise.all(promises);
  } catch (error) {
    console.error('Error processing hashtags:', error);
    throw new Error('No se pudieron procesar los hashtags');
  }
};

/**
 * Obtiene los hashtags mu00e1s populares (trending)
 * @param count Nu00famero de hashtags a obtener
 * @returns Array de hashtags trending con su informaciu00f3n
 */
export const getTrendingHashtags = async (count: number = 10) => {
  try {
    // Consultar los hashtags mu00e1s populares por contador
    const trendingQuery = query(
      collection(db, 'trendingHashtags'),
      orderBy('count', 'desc'),
      limit(count)
    );

    const snapshot = await getDocs(trendingQuery);
    const hashtags = snapshot.docs.map(doc => {
      const data = doc.data();

      // Calcular el score basado en la frecuencia y lo reciente que es
      let score = data.count || 1;
      if (data.lastUsed) {
        const lastUsed = data.lastUsed.toDate ? data.lastUsed.toDate() : new Date(data.lastUsed);
        const now = new Date();
        const hoursSince = Math.max(1, (now.getTime() - lastUsed.getTime()) / (1000 * 60 * 60));

        // Dar mu00e1s peso a hashtags recientes
        score = data.count / Math.sqrt(hoursSince);
      }

      return {
        id: doc.id,
        tag: data.tag || `#${doc.id}`,
        count: data.count || 0,
        lastUsed: data.lastUsed,
        score: score,
        postCount: data.posts?.length || 0
      };
    });

    // Ordenar por score calculado
    return hashtags.sort((a, b) => b.score - a.score);
  } catch (error) {
    console.error('Error getting trending hashtags:', error);
    throw new Error('No se pudieron obtener los hashtags tendencia');
  }
};

/**
 * Obtiene posts que contienen un hashtag especu00edfico
 * @param hashtag Hashtag a buscar (con o sin #)
 * @param maxResults Lu00edmite de posts a obtener
 * @returns Array de posts que contienen el hashtag
 */
export const getPostsByHashtag = async (hashtag: string, maxResults: number = 20) => {
  try {
    // Asegurarse de que el hashtag incluya #
    const normalizedHashtag = hashtag.startsWith('#') ? hashtag : `#${hashtag}`;

    // Consultar posts que contienen el hashtag
    const postsQuery = query(
      collection(db, 'posts'),
      where('hashtags', 'array-contains', normalizedHashtag),
      orderBy('createdAt', 'desc'),
      limit(maxResults)
    );

    const snapshot = await getDocs(postsQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error(`Error getting posts for hashtag ${hashtag}:`, error);
    throw new Error(`No se pudieron obtener los posts con el hashtag ${hashtag}`);
  }
};
